import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const fade = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

interface FullServiceCalloutProps {
  /** "homepage" | "installation" | "refinishing" | "lvp" | "subfloor" | "commercial" | "city" | "hardwood-hub" */
  variant: string;
  cityName?: string;
}

const FullServiceCallout = ({ variant, cityName }: FullServiceCalloutProps) => {
  if (variant === "homepage") {
    return (
      <section className="bg-foreground py-16 md:py-24 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fade} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3 mb-5">
              <ShieldCheck className="w-6 h-6 text-primary flex-shrink-0" />
              <span className="text-[9px] font-bold tracking-[.14em] uppercase text-primary bg-primary/10 rounded-full px-3 py-1.5">
                Licensed Michigan Residential Builder
              </span>
            </div>
            <h2 className="font-display text-[clamp(26px,4.5vw,48px)] font-semibold text-primary-foreground leading-[1.08] mb-3">
              Licensed Michigan Residential Builder
            </h2>
            <p className="font-display text-[clamp(16px,2.5vw,24px)] font-light text-[hsl(var(--gold-300))] mb-8">
              Full-Service Project Management — From Subfloor to Final Trim
            </p>
            <div className="grid md:grid-cols-[1.2fr_1fr] gap-10 md:gap-16">
              <div>
                <p className="text-[14px] md:text-[15px] font-normal text-primary-foreground/70 leading-[1.8] mb-6">
                  We are hardwood flooring specialists first. But as a Licensed Michigan Residential Builder, we can handle everything a complete flooring project requires — without you coordinating a single vendor.
                </p>
                <p className="text-[13px] font-semibold text-primary-foreground/90 mb-4">What that means in practice:</p>
                <ul className="space-y-3">
                  {[
                    "Licensed plumbers for all appliance disconnections and reconnections — gas lines, water lines, valve repair. We never let floor installers handle plumbing.",
                    "A professional moving company clears your furniture and appliances completely before we start. No shoveling furniture around. No sanding grit blown through a half cleared room.",
                    "POD storage coordination if you need temporary offsite storage during the project.",
                    "Piano moving specialists — we have vetted vendors for the moves no one else wants to handle.",
                    "Painting and renovation contractors — when a flooring project reveals a larger scope, we have trusted partners ready.",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-[13px] md:text-[14px] text-primary-foreground/60 leading-relaxed">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-primary-foreground/[.04] border border-primary-foreground/10 rounded-2xl p-7 md:p-9 self-start">
                <p className="text-[11px] font-bold tracking-[.14em] uppercase text-[hsl(var(--gold-400))] mb-4">
                  Especially Valuable For
                </p>
                <ul className="space-y-3">
                  {[
                    "Out-of-state buyers relocating to Michigan",
                    "Homeowners managing a renovation remotely",
                    "New construction clients who need turnkey completion",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-[13px] text-primary-foreground/60 leading-relaxed">
                      <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--gold-400))] mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  if (variant === "installation") {
    return (
      <section className="bg-background py-14 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fade} transition={{ duration: 0.7 }}
            className="border border-primary/20 bg-primary/[.03] rounded-2xl p-7 md:p-10"
          >
            <div className="flex items-center gap-3 mb-5">
              <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0" />
              <h3 className="font-display text-lg md:text-xl font-semibold text-foreground">The Full-Service Difference</h3>
            </div>
            <p className="text-[14px] md:text-[15px] text-muted-foreground leading-[1.8] mb-5">
              Most flooring installers show up and work around whatever is in the room. We don't. As a Licensed Michigan Residential Builder, we approach every installation as a complete project — not just the floor.
            </p>
            <p className="text-[13px] font-semibold text-foreground mb-3">Before we lay a single plank:</p>
            <ul className="space-y-2.5 mb-5">
              {[
                "Licensed plumbers handle all gas and water line disconnections — refrigerator water lines, dishwashers, gas stoves, washing machines. We use licensed tradespeople for every utility connection. No exceptions.",
                "A professional moving company removes your furniture and appliances entirely from the work area. The subfloor gets a full forensic inspection — no surprises, no working around objects.",
                "We identify and repair every subfloor issue before installation begins. A floor installed over a compromised subfloor will fail — regardless of how good the material is.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-[13px] text-muted-foreground leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-[13px] text-muted-foreground leading-relaxed mb-4">
              For out-of-state buyers and relocating homeowners: we can coordinate the complete scope of your project remotely. You don't need to be on-site. We manage every vendor, every trade, every detail and hand you the keys to a finished floor.
            </p>
            <Link to="/subfloor-leveling" className="text-primary text-sm font-medium hover:text-[hsl(var(--gold-300))] underline underline-offset-2 transition-colors">
              Learn about our forensic subfloor preparation process →
            </Link>
          </motion.div>
        </div>
      </section>
    );
  }

  if (variant === "refinishing") {
    return (
      <section className="bg-background py-14 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fade} transition={{ duration: 0.7 }}
            className="border border-primary/20 bg-primary/[.03] rounded-2xl p-7 md:p-10"
          >
            <div className="flex items-center gap-3 mb-5">
              <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0" />
              <h3 className="font-display text-lg md:text-xl font-semibold text-foreground">Full-Service Refinishing</h3>
            </div>
            <p className="text-[14px] md:text-[15px] text-muted-foreground leading-[1.8]">
              Refinishing a home's floors while you're living in it — or doing it before a move — requires serious coordination. We handle it. As a Licensed Michigan Residential Builder we can arrange complete furniture removal by a professional moving company, temporary POD storage if needed, licensed plumbing disconnections for any appliances in the work area, and a vetted painting crew for touch-ups after the floors are done. You focus on the move. We handle the floor.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  if (variant === "lvp") {
    return (
      <section className="bg-background py-14 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fade} transition={{ duration: 0.7 }}
            className="border border-primary/20 bg-primary/[.03] rounded-2xl p-7 md:p-10"
          >
            <div className="flex items-center gap-3 mb-5">
              <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0" />
              <h3 className="font-display text-lg md:text-xl font-semibold text-foreground">Licensed Residential Builder</h3>
            </div>
            <p className="text-[14px] md:text-[15px] text-muted-foreground leading-[1.8]">
              LVP projects frequently involve kitchens and laundry rooms — areas with appliances that require disconnection before work can begin. As a Licensed Michigan Residential Builder, we coordinate licensed plumbers for all gas and water line work. The floor gets done right. The appliances get handled safely.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  if (variant === "subfloor") {
    return (
      <section className="bg-background py-14 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fade} transition={{ duration: 0.7 }}
            className="border border-primary/20 bg-primary/[.03] rounded-2xl p-7 md:p-10"
          >
            <div className="flex items-center gap-3 mb-5">
              <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0" />
              <h3 className="font-display text-lg md:text-xl font-semibold text-foreground">Licensed Residential Builder</h3>
            </div>
            <p className="text-[14px] md:text-[15px] text-muted-foreground leading-[1.8]">
              Subfloor work often requires a completely cleared workspace — more so than any other phase of a flooring project. We coordinate professional furniture removal and appliance disconnection by licensed plumbers before a single bag of SLU is mixed. The subfloor gets the access it needs. The work gets done correctly.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  if (variant === "commercial") {
    return (
      <section className="bg-background py-14 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fade} transition={{ duration: 0.7 }}
            className="border border-primary/20 bg-primary/[.03] rounded-2xl p-7 md:p-10"
          >
            <div className="flex items-center gap-3 mb-5">
              <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0" />
              <h3 className="font-display text-lg md:text-xl font-semibold text-foreground">Licensed Residential Builder</h3>
            </div>
            <p className="text-[14px] md:text-[15px] text-muted-foreground leading-[1.8]">
              Commercial projects require general contractor oversight — permitting, trade coordination, scheduling and compliance. As a Licensed Michigan Residential Builder with commercial flooring experience, we manage the full project scope so building owners and property managers have a single point of accountability.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  if (variant === "city" && cityName) {
    return (
      <section className="bg-foreground py-14 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fade} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3 mb-5">
              <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0" />
              <span className="text-[9px] font-bold tracking-[.14em] uppercase text-primary bg-primary/10 rounded-full px-3 py-1.5">
                Licensed Michigan Residential Builder
              </span>
            </div>
            <h3 className="font-display text-[clamp(22px,3.5vw,36px)] font-semibold text-primary-foreground leading-[1.08] mb-5">
              Full-Service Flooring in <em className="italic font-normal text-[hsl(var(--gold-300))]">{cityName}</em>
            </h3>
            <p className="text-[14px] md:text-[15px] text-primary-foreground/70 leading-[1.8] mb-6 max-w-3xl">
              Whether you are a {cityName} homeowner coordinating a renovation or a buyer relocating to Michigan who needs everything handled remotely, 1 Wood Floors operates as your single point of contact.
            </p>
            <p className="text-[13px] font-semibold text-primary-foreground/90 mb-4">
              As a Licensed Michigan Residential Builder, we coordinate every trade your flooring project requires:
            </p>
            <ul className="space-y-2.5 mb-6">
              {[
                "Licensed plumbers for appliance disconnection and reconnection",
                "Professional furniture and appliance moving and full workspace clearance",
                "POD storage coordination",
                "Piano moving specialists",
                "Painting and renovation contractors for larger project scopes",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-[13px] text-primary-foreground/60 leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-[14px] text-primary-foreground/70 leading-[1.8]">
              You do not need to manage a single vendor. We handle the complete scope from the first site visit through final walkthrough.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  if (variant === "hardwood-hub") {
    return (
      <section className="bg-background py-14 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fade} transition={{ duration: 0.7 }}
            className="border border-primary/20 bg-primary/[.03] rounded-2xl p-7 md:p-10"
          >
            <div className="flex items-center gap-3 mb-5">
              <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0" />
              <h3 className="font-display text-lg md:text-xl font-semibold text-foreground">One Contractor. Everything Handled.</h3>
            </div>
            <p className="text-[14px] md:text-[15px] text-muted-foreground leading-[1.8]">
              Hardwood flooring projects in Michigan homes frequently involve appliance moves, furniture coordination, subfloor repairs and finish work that extends beyond the floor itself. As a Licensed Michigan Residential Builder, we manage the complete scope — licensed plumbing, professional moving, storage, painting and renovation coordination. Especially valuable for out-of-state buyers and relocating homeowners who need a turnkey project completed without being present.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return null;
};

export default FullServiceCallout;
